import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Calculator, Info, Star, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Public wild-card explainer card.
 * Fetches /api/public/qf/wildcard-report and renders a compact leaderboard
 * of the 6 wild-card candidates (3rd + 4th of every group) ranked by AGD,
 * so fans can see exactly why each pick made the cut.
 * Returns null if the report is hidden by admin (404) or empty.
 */
export default function WildCardReport() {
  const [rows, setRows] = useState(null);
  const [explanation, setExplanation] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/qf/wildcard-report");
        if (!alive) return;
        setRows(data.candidates || []);
        setExplanation(data.explanation || "");
      } catch (e) {
        if (alive) setRows([]);  // hidden or unavailable
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <Card className="p-5 bg-[#0f1a15] border-white/10" data-testid="wildcard-report">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="w-4 h-4 text-[#F4B400]" />
        <h2 className="font-anton text-xl uppercase tracking-tight flex-1">How The Wild Cards Are Picked</h2>
        <Link to="/wildcard-story" data-testid="wildcard-story-cta"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded border border-[#F4B400]/40 text-[#F4B400] hover:bg-[#F4B400]/10">
          <Instagram className="w-3 h-3" /> Story Mode
        </Link>
      </div>
      <div className="flex items-start gap-2 text-[11px] text-zinc-400 mb-4">
        <Info className="w-3 h-3 mt-0.5 shrink-0 text-zinc-500" />
        <span>{explanation}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
            <tr className="border-b border-white/5">
              <th className="text-left py-2 px-2 w-8">#</th>
              <th className="text-left py-2 px-2">Team</th>
              <th className="text-center py-2 px-2">Grp / Pos</th>
              <th className="text-center py-2 px-2">Games W/L</th>
              <th className="text-center py-2 px-2">GD</th>
              <th className="text-center py-2 px-2">Ties</th>
              <th className="text-right py-2 px-2">AGD</th>
              <th className="text-right py-2 px-2 min-w-[110px]">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const wc = !!r.picked;
              return (
                <tr key={r.team_id} className={`border-b border-white/5 ${wc ? "bg-[#F4B400]/5" : "hover:bg-white/2"}`} data-testid={`wildcard-row-${r.rank}`}>
                  <td className={`py-2 px-2 font-bold ${wc ? "text-[#F4B400]" : "text-zinc-500"}`}>{r.rank}</td>
                  <td className="py-2 px-2">
                    <span className={`${wc ? "text-white font-semibold" : "text-zinc-300"}`}>{r.team_name}</span>
                  </td>
                  <td className="py-2 px-2 text-center text-xs text-zinc-400">Grp {r.group} · Pos {r.position_in_group}</td>
                  <td className="py-2 px-2 text-center text-xs text-zinc-400 tabular-nums">{r.games_won}/{r.games_lost}</td>
                  <td className={`py-2 px-2 text-center font-bold tabular-nums ${
                    r.game_diff > 0 ? "text-emerald-400" : r.game_diff < 0 ? "text-red-400" : "text-zinc-400"
                  }`}>{r.game_diff > 0 ? "+" : ""}{r.game_diff}</td>
                  <td className="py-2 px-2 text-center text-xs text-zinc-500 tabular-nums">{r.ties_played}</td>
                  <td className="py-2 px-2 text-right font-bold tabular-nums text-white">{r.avg_game_diff.toFixed(2)}</td>
                  <td className="py-2 px-2 text-right">
                    {wc ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F4B400] text-black text-[9px] uppercase tracking-widest font-bold">
                        <Star className="w-2.5 h-2.5" /> {r.picked}
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-600 italic">missed cut</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-zinc-500 mt-3">
        AGD = Game Difference ÷ Ties Played. The top 2 rows earn Seeds #7 and #8. Auto qualifiers (positions #1–#6) are fixed by group finish and never re-ranked.
      </div>
    </Card>
  );
}
